import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { ProjectGq } from '../../../../../../../../graphql/projects/models/ProjectGq';
import useCopyComboRequestGq from '../../../../../../../../graphql/projects/mutations/googleAdsCombo/useCopyComboRequestGq';
import { projectsApiService } from '../../../../../../../../graphql/projects/projectsApiService';
import { useDebounceValue } from '../../../../../../../../logic/otherUtilities/useDebounceValue';
import VoxelDialog from '../../../../../../../voxel/Dialogs/VoxelDialog';
import VoxelDialogActionSubmit from '../../../../../../../voxel/Dialogs/VoxelDialogActionSubmit';
import VoxelDialogActions from '../../../../../../../voxel/Dialogs/VoxelDialogActions';
import VoxelDialogContent from '../../../../../../../voxel/Dialogs/VoxelDialogContent';
import VoxelCheckbox from '../../../../../../../voxel/Forms/VoxelCheckbox';
import VoxelText from '../../../../../../../voxel/Text/VoxelText';
import SearchInfoTooltipContent from '../../../../../../../voxel/Tooltip/Content/SearchInfoTooltipContent';
import VoxelTooltipLong from '../../../../../../../voxel/Tooltip/VoxelTooltipLong';
import VoxelDivider from '../../../../../../../voxel/VoxelDivider';
import ComboButtonSection from '../../../../../../Buttons/ComboButtonSection';
import InfoIcon from '../../../../../../../voxel/Icons/InfoIcon';
import LoadingScreen from '../../../../../../LoadingScreen/LoadingScreen';
import ComboConversionStatus from './components/ComboConversionStatus';
import ComboSearchSettings from './components/ComboSearchSettings';
import ComboSelectBox from './components/ComboSelectBox';
import ComboSelectedSection from './components/ComboSelectedSection';
import ComboWordCounter from './components/ComboWordCounter';
import WordGroupList from './components/WordGroupList/WordGroupList';
import { useCalculateKeywords } from './logic/useCalculateKeywords';
import { useExecute } from './logic/useExecute';
import { useSetUpComboForm } from './logic/useSetUpComboForm';
import { SetFormState } from './models/SetFormState';

export interface EditComboRequestViewProps {
  comboRequestId: number;
  project: ProjectGq;
  goToCombo: (id: number) => void;
  handleClose: () => void;
  reload: () => Promise<unknown>;
}

const EditComboRequestView: React.FC<EditComboRequestViewProps> = ({
  comboRequestId,
  project,
  goToCombo,
  handleClose,
  reload,
}) => {
  // Get data
  const comboRequestsQuery = projectsApiService.queries.googleAds.useGetComboRequest({
    comboRequestId,
    projectId: project.id,
  });
  const comboRequest = comboRequestsQuery.result;

  // Form state
  const formikProps = useSetUpComboForm(project, comboRequest);

  const wordCount = useCalculateKeywords(formikProps);
  const executeState = useExecute(formikProps, () => handleClose(), project.id, wordCount, reload);
  const setFormState: SetFormState = useCallback(
    (stateFn) => formikProps.setFormikState((prev) => ({ ...prev, values: stateFn(prev.values) })),
    []
  );

  // Automatic saving
  const [savingDraft, setSavingDraft] = useState(false);
  const debouncedState = useDebounceValue(formikProps.values, 3 * 1000);
  useEffect(() => {
    formikProps.validateForm();
    if (!formikProps.dirty) return; // Don't bother if it's not dirty
    if (!formikProps.isValid) return; // Don't bother if it's not valid
    if (!(debouncedState === formikProps.values)) return; // Don't bother if values are being changed
    if (formikProps.isSubmitting) return; // Avoid double saving, that creates a lot of issues

    setSavingDraft(true);
    formikProps.submitForm().finally(() => setSavingDraft(false));
  }, [debouncedState, formikProps.dirty, formikProps.isValid, formikProps.isSubmitting]);

  // Copy combo request
  const [copying, setCopying] = useState(false);
  const [copiedId, setCopiedId] = useState(undefined);
  const copyMutation = useCopyComboRequestGq();
  const handleCopyComboRequest = () => {
    setCopying(true);
    return copyMutation({ id: comboRequestId, projectId: project.id })
      .then((result) => {
        setCopiedId(result.id);
      })
      .catch(() => setCopying(false));
  };

  // Save draft before closing
  const handleCloseAndSaveDraft = () => {
    if (comboRequest.status === 'draft' && formikProps.dirty) {
      formikProps.submitForm().then(() => handleClose());
    }
    handleClose();
  };

  // Render
  if (!comboRequest) {
    return (
      <>
        <LoadingScreen />
      </>
    );
  }

  if (!!copiedId)
    return (
      <VoxelDialog
        open
        closeOnBackdrop={false}
        onClose={() => {
          goToCombo(copiedId);
          setCopiedId(undefined);
        }}
        title="Combination copied"
      >
        <VoxelDialogContent>You can now edit the copied combination</VoxelDialogContent>
        <VoxelDialogActions>
          <VoxelDialogActionSubmit
            title="Continue"
            fullWidth
            onClick={() => {
              goToCombo(copiedId);
              setCopiedId(undefined);
              setCopying(false);
            }}
          />
        </VoxelDialogActions>
      </VoxelDialog>
    );

  if (executeState.executed)
    return (
      <VoxelDialog open closeOnBackdrop={false} onClose={handleClose} title="Combination created">
        <VoxelDialogContent>Your keywords are being added to the project</VoxelDialogContent>
        <VoxelDialogActions>
          <VoxelDialogActionSubmit title="Continue" fullWidth onClick={handleClose} />
        </VoxelDialogActions>
      </VoxelDialog>
    );

  return (
    <VoxelDialog
      open
      closeOnBackdrop={false}
      onClose={handleCloseAndSaveDraft}
      minWClass="min-w-[400px] sm:min-w-[600px] md:min-w-[700px] lg:min-w-[1000px] xl:min-w-[1200px] 2xl:min-w-[1400px]"
    >
      <VoxelDialogContent className="flex flex-col gap-6 pt-6">
        <div className="flex flex-row items-center gap-1">
          <VoxelText variant="title2">Combination</VoxelText>
          <ComboConversionStatus comboRequest={comboRequest} />
        </div>
        <ComboSearchSettings comboRequest={comboRequest} formBag={formikProps} />
        <VoxelDivider />
        <div className="flex flex-col gap-6">
          {/* Word groups */}
          <div className="flex flex-col gap-6">
            <div>
              <div className="flex flex-row items-center gap-4">
                <VoxelText variant="title3" paddingBot>
                  Word groups
                  <span className="ml-1">
                    <VoxelTooltipLong message={<SearchInfoTooltipContent />} placement="bottom-end">
                      <InfoIcon size="lg" />
                    </VoxelTooltipLong>
                  </span>
                </VoxelText>
              </div>
              <VoxelText variant="bread-normal" paddingBot>
                Add word groups to be combined
              </VoxelText>
            </div>
            <div>
              <WordGroupList
                formState={formikProps}
                wordGroups={formikProps.values.wordGroups}
                disabled={!!comboRequest.processingStartTime}
                setFormState={setFormState}
              />
            </div>
            {!!formikProps?.errors?.wordGroups?.length && (
              <VoxelText variant="form-caption" color="error">
                {formikProps?.errors?.wordGroups}
              </VoxelText>
            )}
          </div>
          {/* Combinations */}
          <VoxelDivider />
          <div>
            <div>
              <VoxelText variant="title3" paddingBot>
                Combinations
              </VoxelText>
              <VoxelText variant="bread-normal" paddingBot>
                Select combinations to be included
              </VoxelText>
            </div>
            <div className="w-full flex flex-row gap-8">
              <div className="w-full border rounded-lg p-2">
                <ComboSelectBox
                  wordGroups={formikProps.values.wordGroups}
                  combinations={formikProps.values.comboCombinations}
                  setFormState={setFormState}
                  disabled={!!comboRequest.processingStartTime}
                />
              </div>
              <div className="w-full">
                <ComboSelectedSection
                  disabled={!!comboRequest.processingStartTime}
                  setFormState={setFormState}
                  combinations={formikProps.values.comboCombinations}
                  wordGroups={formikProps.values.wordGroups}
                />
              </div>
              <div className="w-full rounded-lg">
                <ComboWordCounter
                  wordCount={wordCount}
                  executed={!!comboRequest.processingStartTime}
                />
              </div>
            </div>
          </div>
          <VoxelDivider />

          <VoxelCheckbox
            color="slate"
            label={'Create and apply matching tag structure'}
            checked={formikProps.values.useComboTagging}
            disabled={!!comboRequest.processingStartTime}
            onChange={(value: boolean) => formikProps.setFieldValue('useComboTagging', value)}
          />
        </div>
      </VoxelDialogContent>
      <VoxelDialogActions>
        <ComboButtonSection
          wordCount={wordCount}
          formState={formikProps}
          copying={copying}
          copyRequest={handleCopyComboRequest}
          executeState={executeState}
          comboRequest={comboRequest}
          handleClose={handleCloseAndSaveDraft}
        />
      </VoxelDialogActions>
    </VoxelDialog>
  );
};

export default EditComboRequestView;
